/**
 * Athletic Availability (RSVP) entity contract.
 *
 * An athlete's In / Out / Maybe response — with an optional note — to a synced
 * athletic game or practice. One response per (game, athlete).
 *
 * Wire shape only: like every other contract in this package the public-facing
 * ids are public CUIDs, never the internal DB bigints (backend maps `publicId`
 * → `id` at the projector boundary). The underlying `AthleticAvailability`
 * Prisma model keys on INTERNAL foreign keys (`AthleticGame.id`,
 * `UsersOnSchools.id`) — Blackbaud platform ids are reference-only and never a
 * join key (plan 2026-06-16-003, KTD1/KTD5).
 *
 * The note is untrusted, minor-authored text: the `.max()` cap is enforced both
 * here (request validation) and server-side, and the value is escaped/rendered
 * as plain text by every consumer (R5).
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-16-003-feat-athletics-availability-rsvp-plan.md (U4).
 */

import { z } from 'zod'

// ---------------------------------------------------------------------------
// Status enum
// ---------------------------------------------------------------------------

/** An athlete's availability response. IN / OUT / MAYBE only (R4). */
export const AthleticAvailabilityStatusSchema = z.enum(['IN', 'OUT', 'MAYBE'])
export type AthleticAvailabilityStatus = z.infer<typeof AthleticAvailabilityStatusSchema>

// ---------------------------------------------------------------------------
// Note cap
// ---------------------------------------------------------------------------

/**
 * Server-enforced max length for an availability note. Mirrored by the
 * `AthleticAvailability.note` column and the write-path validation (R5).
 */
export const ATHLETIC_AVAILABILITY_NOTE_MAX = 500

// ---------------------------------------------------------------------------
// Entity
// ---------------------------------------------------------------------------

/**
 * A single athlete's availability response. Always sourced from a projector;
 * never a raw DB row. `gameId` and `userId` are public CUIDs.
 */
export const AthleticAvailabilitySchema = z.object({
  /** Public CUID of this response row — never the internal DB bigint. */
  id: z.string(),
  /** Public CUID of the athletic game/practice this response is for. */
  gameId: z.string(),
  /**
   * Public CUID of the responding athlete (school-user). Null only in the rare
   * degraded case where the write succeeded but the responder's public id could
   * not be re-resolved (transient DB error) — distinguishable from a real id and
   * never an empty string (R1).
   */
  userId: z.string().nullable(),
  /** The athlete's response. */
  status: AthleticAvailabilityStatusSchema,
  /** Optional, length-capped, untrusted note. Rendered as plain text. */
  note: z.string().max(ATHLETIC_AVAILABILITY_NOTE_MAX).nullable().optional(),
  /** ISO 8601 timestamp of the last update to this response. */
  updatedAt: z.string().nullable().optional()
})
export type AthleticAvailability = z.infer<typeof AthleticAvailabilitySchema>
